<script setup lang="ts">
import { useForm } from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3'
import { ChevronDown, ExternalLink, Eye, FileText, Paperclip, PhoneCall, ScanEye, Trash2 } from 'lucide-vue-next'
import { computed, reactive, ref } from 'vue'

import AppTooltip from '@/components/AppTooltip.vue'
import DialogLoadingOverlay from '@/components/DialogLoadingOverlay.vue'
import DocStatusBadge from '@/components/applications/DocStatusBadge.vue'
import DocuSignButton from '@/components/applications/DocuSignButton.vue'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useDialog } from '@/composables/useDialog'
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '@/lib/constants'
import { formatDateTime, formatLabel } from '@/lib/utils'
import {
  acceptLoanApplicationDocumentPath,
  bulkReviewLoanApplicationDocumentsPath,
  loanApplicationDocumentPath,
  loanApplicationDocumentsPath,
  rejectLoanApplicationDocumentPath,
  remindBankStatementLoanApplicationDocumentsPath,
  requestBankStatementLoanApplicationDocumentsPath,
  requestInfoLoanApplicationDocumentsPath,
  uploadStatementLoanApplicationDocumentsPath,
} from '@/routes'

interface Document {
  id: number
  name: string
  status: string
  rejection_reason: string | null
  reviewed_at: string | null
  created_at: string
  file_url: string | null
  file_name: string | null
  bank_statement: boolean
}

interface BankStatementLinkOption {
  type: string
  label: string
  months: number
}

interface Props {
  applicationId: string
  documents: Document[]
  readonly: boolean
  uploadUrl: string
  optionalDocNames: string[]
  bankStatementLinkOptions: BankStatementLinkOption[]
  infoRequestedAt: string | null
  bankStatementReminderSentAt: string | null
}

const props = defineProps<Props>()

const { confirm } = useDialog()

// ── Status helpers ───────────────────────────────────────────────────────────

const hasPendingUploads = computed(() => props.documents.some((d) => d.status === 'requested'))
const uploadedDocs = computed(() => props.documents.filter((d) => d.status === 'uploaded'))
const statementDocs = computed(() => props.documents.filter((d) => d.status === 'statement_requested'))

// ── Request more info (customer call-back) ────────────────────────────────────

const INFO_REQUIRED_COOLDOWN_MS = 24 * 60 * 60 * 1000

const infoRequestedRecently = computed(() => {
  if (!props.infoRequestedAt) return false
  return Date.now() - new Date(props.infoRequestedAt).getTime() < INFO_REQUIRED_COOLDOWN_MS
})

const requestInfoProcessing = ref(false)

async function requestInfo() {
  if (infoRequestedRecently.value || requestInfoProcessing.value) return
  const ok = await confirm({
    title: 'Send "more info required" SMS?',
    description: 'The customer will be asked to call us to verify some info about their documents.',
    confirmLabel: 'Send SMS',
    variant: 'destructive',
  })
  if (!ok) return
  requestInfoProcessing.value = true
  router.post(
    requestInfoLoanApplicationDocumentsPath(props.applicationId),
    {},
    {
      preserveScroll: true,
      onFinish: () => {
        requestInfoProcessing.value = false
      },
    },
  )
}

// ── Request bank statement ────────────────────────────────────────────────────

const bankStatementProcessing = ref<string | null>(null)

function openUploadLink() {
  window.open(props.uploadUrl, '_blank')
}

function requestBankStatement(type: string) {
  if (bankStatementProcessing.value !== null) return
  bankStatementProcessing.value = type
  router.post(
    requestBankStatementLoanApplicationDocumentsPath(props.applicationId),
    { type },
    {
      preserveScroll: true,
      onFinish: () => {
        bankStatementProcessing.value = null
      },
    },
  )
}

// ── Bank statement reminder ───────────────────────────────────────────────────

const BANK_STATEMENT_REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000

const reminderRecently = computed(() => {
  if (!props.bankStatementReminderSentAt) return false
  return Date.now() - new Date(props.bankStatementReminderSentAt).getTime() < BANK_STATEMENT_REMINDER_COOLDOWN_MS
})

const remindProcessing = ref(false)

async function remindBankStatement() {
  if (statementDocs.value.length === 0 || reminderRecently.value || remindProcessing.value) return
  const ok = await confirm({
    title: 'Send bank statement reminder SMS?',
    description: 'The customer will be sent a reminder to upload their pending bank statement(s).',
    confirmLabel: 'Send SMS',
    variant: 'destructive',
  })
  if (!ok) return
  remindProcessing.value = true
  router.post(
    remindBankStatementLoanApplicationDocumentsPath(props.applicationId),
    {},
    {
      preserveScroll: true,
      onFinish: () => {
        remindProcessing.value = false
      },
    },
  )
}

// ── Single doc preview dialog ────────────────────────────────────────────────

const previewDoc = ref<Document | null>(null)
const singleRejectMode = ref(false)
const singleRejectReason = ref('')
const singleProcessing = ref(false)

function openPreview(doc: Document) {
  previewDoc.value = doc
  singleRejectMode.value = false
  singleRejectReason.value = ''
}

function closePreview() {
  previewDoc.value = null
  singleRejectMode.value = false
  singleRejectReason.value = ''
  singleProcessing.value = false
}

function cancelSingleReject() {
  singleRejectMode.value = false
  singleRejectReason.value = ''
}

function handlePreviewOpenChange(open: boolean) {
  if (!open && !singleProcessing.value) closePreview()
}

function singleAccept() {
  if (!previewDoc.value || singleProcessing.value) return
  singleProcessing.value = true
  router.patch(
    acceptLoanApplicationDocumentPath(props.applicationId, previewDoc.value.id),
    {},
    { preserveScroll: true, onFinish: () => closePreview() },
  )
}

function singleReject() {
  if (!previewDoc.value || !singleRejectReason.value.trim() || singleProcessing.value) return
  singleProcessing.value = true
  router.patch(
    rejectLoanApplicationDocumentPath(props.applicationId, previewDoc.value.id),
    { rejection_reason: singleRejectReason.value },
    { preserveScroll: true, onFinish: () => closePreview() },
  )
}

// ── Preview All / bulk review dialog ─────────────────────────────────────────

type Decision = { action: 'accept' | 'reject'; rejection_reason: string }

const previewAllOpen = ref(false)
const decisions = reactive<Record<number, Decision>>({})
const bulkProcessing = ref(false)

const hasDecisions = computed(() => Object.keys(decisions).length > 0)
const canSave = computed(
  () =>
    hasDecisions.value &&
    Object.values(decisions).every(
      (d) => d.action === 'accept' || d.rejection_reason.trim().length > 0,
    ),
)

function openPreviewAll() {
  Object.keys(decisions).forEach((k) => delete decisions[Number(k)])
  previewAllOpen.value = true
}

function handlePreviewAllOpenChange(open: boolean) {
  if (!open && !bulkProcessing.value) previewAllOpen.value = false
}

function toggleDecision(docId: number, action: 'accept' | 'reject') {
  if (decisions[docId]?.action === action) {
    delete decisions[docId]
  } else {
    decisions[docId] = { action, rejection_reason: decisions[docId]?.rejection_reason ?? '' }
  }
}

function saveDecisions() {
  if (!canSave.value || bulkProcessing.value) return
  bulkProcessing.value = true
  const list = Object.entries(decisions).map(([id, d]) => ({
    id: Number(id),
    action: d.action,
    rejection_reason: d.rejection_reason,
  }))
  router.post(
    bulkReviewLoanApplicationDocumentsPath(props.applicationId),
    { decisions: list },
    {
      preserveScroll: true,
      onFinish: () => {
        bulkProcessing.value = false
        previewAllOpen.value = false
        Object.keys(decisions).forEach((k) => delete decisions[Number(k)])
      },
    },
  )
}

// ── Delete (manager-attached) ─────────────────────────────────────────────────

async function destroy(doc: Document) {
  const ok = await confirm({
    title: 'Remove document?',
    description: formatLabel(doc.name),
    confirmLabel: 'Remove',
    variant: 'destructive',
  })
  if (!ok) return
  router.delete(loanApplicationDocumentPath(props.applicationId, doc.id), { preserveScroll: true })
}

function isDeletable(doc: Document) {
  return doc.status === 'attached' || (doc.status === 'accepted' && doc.bank_statement)
}

// ── File validation ───────────────────────────────────────────────────────────

function validateFile(file: File): string | null {
  if (!ALLOWED_MIME_TYPES.includes(file.type as (typeof ALLOWED_MIME_TYPES)[number]))
    return 'Invalid file type. Only PDF, JPEG, and PNG are accepted.'
  if (file.size > MAX_FILE_SIZE_BYTES) return `File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`
  return null
}

// ── Attach file form ─────────────────────────────────────────────────────────

const attachForm = useForm({ name: '', file: null as File | null })
const attachFileError = ref<string | null>(null)

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  attachForm.file = null
  attachFileError.value = null
  if (!file) return
  const error = validateFile(file)
  if (error) {
    attachFileError.value = error
    input.value = ''
    return
  }
  attachForm.file = file
}

function submitAttach() {
  attachForm.post(loanApplicationDocumentsPath(props.applicationId), {
    preserveScroll: true,
    onSuccess: () => {
      attachForm.reset()
      attachFileError.value = null
    },
  })
}

// ── Upload bank statement form ────────────────────────────────────────────────

const statementForm = useForm({ name: '', file: null as File | null })
const statementFileError = ref<string | null>(null)

function onStatementFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  statementForm.file = null
  statementFileError.value = null
  if (!file) return
  const error = validateFile(file)
  if (error) {
    statementFileError.value = error
    input.value = ''
    return
  }
  statementForm.file = file
}

function submitStatement() {
  statementForm.post(uploadStatementLoanApplicationDocumentsPath(props.applicationId), {
    preserveScroll: true,
    onSuccess: () => {
      statementForm.reset()
      statementFileError.value = null
    },
  })
}
</script>

<template>
  <div class="space-y-4">
    <!-- Actions bar -->
    <div class="bg-muted/40 flex flex-wrap items-center justify-between gap-2 rounded-md border px-4 py-2.5">
      <!-- Left: Request bank statement + Request more info + DocuSign -->
      <div class="flex shrink-0 items-center gap-2">
        <DropdownMenu v-if="!readonly">
          <DropdownMenuTrigger as-child>
            <Button
              size="sm"
              variant="outline"
              class="gap-1.5 text-xs"
              :disabled="bankStatementProcessing !== null"
            >
              <FileText class="size-3.5" />
              {{ bankStatementProcessing ? 'Requesting…' : 'Request Bank Statement' }}
              <ChevronDown class="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              v-for="option in bankStatementLinkOptions"
              :key="option.type"
              @click="requestBankStatement(option.type)"
            >
              {{ option.label }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <AppTooltip
          v-if="!readonly && statementDocs.length > 0 && reminderRecently"
          content-class="max-w-xs text-center"
        >
          <Button size="sm" variant="outline" class="gap-1.5 text-xs" disabled>
            <FileText class="size-3.5" />
            Remind
          </Button>
          <template #content>
            <p>Already sent on {{ formatDateTime(bankStatementReminderSentAt!) }}</p>
          </template>
        </AppTooltip>
        <AppTooltip v-else-if="!readonly && statementDocs.length > 0" content="Send Bank Statement Upload Reminder SMS">
          <Button
            size="sm"
            variant="outline"
            class="gap-1.5 text-xs"
            :disabled="remindProcessing"
            @click="remindBankStatement"
          >
            <FileText class="size-3.5" />
            {{ remindProcessing ? 'Sending…' : 'Remind' }}
          </Button>
        </AppTooltip>

        <AppTooltip v-if="!readonly && infoRequestedRecently" content-class="max-w-xs text-center">
          <Button size="sm" variant="outline" class="gap-1.5 text-xs" disabled>
            <PhoneCall class="size-3.5" />
            Request More Info
          </Button>
          <template #content>
            <p>Already sent on {{ formatDateTime(infoRequestedAt!) }}</p>
          </template>
        </AppTooltip>
        <AppTooltip v-else-if="!readonly" content="Send More Doc Info Required SMS">
          <Button
            size="sm"
            variant="outline"
            class="gap-1.5 text-xs"
            :disabled="requestInfoProcessing"
            @click="requestInfo"
          >
            <PhoneCall class="size-3.5" />
            {{ requestInfoProcessing ? 'Sending…' : 'Request More Info' }}
          </Button>
        </AppTooltip>

        <DocuSignButton v-if="!readonly" :application-id="applicationId" :documents="documents" />
      </div>

      <!-- Right: Upload Link + Review All -->
      <div class="flex shrink-0 items-center gap-2">
        <Button
          v-if="hasPendingUploads"
          size="sm"
          variant="outline"
          class="gap-1.5 text-xs"
          @click="openUploadLink"
        >
          <ExternalLink class="size-3.5" />
          Upload Link
        </Button>

        <Button
          v-if="!readonly && documents.some((d) => d.file_url)"
          size="sm"
          variant="outline"
          class="gap-1.5 text-xs"
          @click="openPreviewAll"
        >
          <ScanEye class="size-3.5" />
          Review All
        </Button>
      </div>
    </div>

    <!-- Documents table -->
    <div class="overflow-x-auto rounded-md border">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-muted/50 border-b">
            <th class="text-muted-foreground px-4 py-3 text-left font-medium">Document</th>
            <th class="text-muted-foreground px-4 py-3 text-left font-medium">Status</th>
            <th class="text-muted-foreground px-4 py-3 text-left font-medium">Date</th>
            <th class="text-muted-foreground px-4 py-3 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="documents.length === 0">
            <tr>
              <td colspan="4" class="text-muted-foreground px-4 py-10 text-center text-sm">
                No documents yet.
              </td>
            </tr>
          </template>
          <tr
            v-for="doc in documents"
            :key="doc.id"
            class="hover:bg-muted/30 border-b transition-colors last:border-0"
          >
            <!-- Name + rejection reason -->
            <td class="px-4 py-3">
              <div class="font-medium">{{ formatLabel(doc.name) }}</div>
              <div v-if="doc.rejection_reason" class="text-destructive mt-0.5 text-xs">
                {{ doc.rejection_reason }}
              </div>
            </td>

            <!-- Status badge -->
            <td class="px-4 py-3">
              <DocStatusBadge :status="doc.status" />
            </td>

            <!-- Date -->
            <td class="text-muted-foreground px-4 py-3 text-xs">
              {{ doc.reviewed_at ? formatDateTime(doc.reviewed_at) : formatDateTime(doc.created_at) }}
            </td>

            <!-- Actions -->
            <td class="px-4 py-3 text-right">
              <div class="flex items-center justify-end gap-2">
                <!-- View / preview button (if file exists) -->
                <Button
                  v-if="doc.file_url"
                  size="sm"
                  variant="ghost"
                  class="text-muted-foreground hover:text-foreground gap-1.5 px-2"
                  @click="openPreview(doc)"
                >
                  <Eye class="size-3.5" />
                  View
                </Button>

                <!-- Delete for attached (manager-uploaded) or uploaded bank statements -->
                <Button
                  v-if="!readonly && isDeletable(doc)"
                  size="sm"
                  variant="ghost"
                  class="text-muted-foreground hover:text-destructive size-7 p-0"
                  @click="destroy(doc)"
                >
                  <Trash2 class="size-3.5" />
                </Button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Bottom forms row -->
    <div v-if="!readonly" class="flex flex-wrap gap-4">
      <!-- Attach document (manager) -->
      <div class="min-w-0 flex-1 rounded-md border p-4">
        <div class="mb-3 flex items-center gap-2 text-sm font-medium">
          <Paperclip class="text-muted-foreground size-4" />
          Attach document
        </div>
        <form class="flex flex-wrap items-end gap-3" @submit.prevent="submitAttach">
          <p v-if="attachFileError" class="text-destructive w-full text-xs">{{ attachFileError }}</p>
          <div class="grid gap-1.5">
            <Label class="text-xs">Document name</Label>
            <Input v-model="attachForm.name" placeholder="e.g. Photo ID" class="w-56" />
          </div>
          <div class="grid gap-1.5">
            <Label class="text-xs">File (PDF, JPEG, PNG — max 10 MB)</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png" class="w-56" @change="onFileChange" />
          </div>
          <Button
            type="submit"
            size="sm"
            :disabled="!attachForm.name || !attachForm.file || !!attachFileError || attachForm.processing"
          >
            Attach
          </Button>
        </form>
      </div>

      <!-- Upload bank statement (only when statement-requested docs exist) -->
      <div v-if="statementDocs.length > 0" class="min-w-0 flex-1 rounded-md border p-4">
        <div class="mb-3 flex items-center gap-2 text-sm font-medium">
          <FileText class="text-muted-foreground size-4" />
          Upload bank statement
        </div>
        <form class="flex flex-wrap items-end gap-3" @submit.prevent="submitStatement">
          <p v-if="statementFileError" class="text-destructive w-full text-xs">{{ statementFileError }}</p>
          <div class="grid gap-1.5">
            <Label class="text-xs">Statement</Label>
            <Select v-model="statementForm.name">
              <SelectTrigger class="w-56">
                <SelectValue placeholder="Select statement…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="doc in statementDocs" :key="doc.id" :value="doc.name">
                  {{ formatLabel(doc.name) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-1.5">
            <Label class="text-xs">File (PDF, JPEG, PNG — max 10 MB)</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png" class="w-full sm:w-56" @change="onStatementFileChange" />
          </div>
          <Button
            type="submit"
            size="sm"
            :disabled="!statementForm.name || !statementForm.file || !!statementFileError || statementForm.processing"
          >
            Upload
          </Button>
        </form>
      </div>
    </div>

    <!-- ── Single doc preview dialog ───────────────────────────────────────── -->
    <Dialog :open="previewDoc !== null" @update:open="handlePreviewOpenChange">
      <DialogContent class="flex max-h-[90vh] flex-col sm:max-w-3xl">
        <DialogLoadingOverlay :show="singleProcessing" message="Processing…" />
        <DialogHeader>
          <DialogTitle class="truncate pr-6">{{ previewDoc ? formatLabel(previewDoc.name) : '' }}</DialogTitle>
        </DialogHeader>

        <!-- File embed -->
        <div class="bg-muted min-h-0 flex-1 overflow-hidden rounded-md border">
          <iframe
            v-if="previewDoc?.file_url"
            :src="previewDoc.file_url"
            class="h-full min-h-[60vh] w-full"
            frameborder="0"
          />
        </div>

        <!-- Fallback open link -->
        <div class="flex items-center justify-between pt-1">
          <a
            v-if="previewDoc?.file_url"
            :href="previewDoc.file_url"
            target="_blank"
            class="text-primary inline-flex items-center gap-1 text-xs hover:underline"
          >
            <ExternalLink class="size-3" />
            Open in new tab
          </a>
          <DocStatusBadge v-if="previewDoc" :status="previewDoc.status" />
        </div>

        <!-- Accept / Reject controls (uploaded only, non-readonly) -->
        <template v-if="!readonly && previewDoc?.status === 'uploaded'">
          <div v-if="!singleRejectMode" class="flex gap-2 pt-1">
            <Button
              class="flex-1"
              variant="outline"
              :disabled="singleProcessing"
              @click="singleAccept"
            >
              Accept
            </Button>
            <Button
              class="flex-1"
              variant="destructive"
              :disabled="singleProcessing"
              @click="singleRejectMode = true"
            >
              Reject
            </Button>
          </div>

          <div v-else class="space-y-2 pt-1">
            <Label class="text-xs">Rejection reason</Label>
            <Textarea
              v-model="singleRejectReason"
              placeholder="e.g. Document is illegible"
              :rows="2"
              class="border-destructive/50 bg-destructive/5 focus-visible:ring-destructive/50"
            />
            <div class="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                class="flex-1"
                @click="cancelSingleReject"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                class="flex-1"
                :disabled="!singleRejectReason.trim() || singleProcessing"
                @click="singleReject"
              >
                Confirm Reject
              </Button>
            </div>
          </div>
        </template>
      </DialogContent>
    </Dialog>

    <!-- ── Review All dialog ────────────────────────────────────────────────── -->
    <Dialog :open="previewAllOpen" @update:open="handlePreviewAllOpenChange">
      <DialogContent class="flex max-h-[90vh] flex-col sm:max-w-5xl">
        <DialogLoadingOverlay :show="bulkProcessing" message="Saving changes…" />
        <DialogHeader>
          <DialogTitle>Review Documents</DialogTitle>
        </DialogHeader>

        <!-- Doc list (only docs with files) -->
        <div class="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
          <div
            v-for="doc in documents.filter((d) => d.file_url)"
            :key="doc.id"
            class="rounded-md border"
            :class="{
              'border-green-400 dark:border-green-700': decisions[doc.id]?.action === 'accept',
              'border-destructive/50': decisions[doc.id]?.action === 'reject',
            }"
          >
            <!-- File preview -->
            <div class="bg-muted">
              <iframe :src="doc.file_url!" class="h-[50vh] w-full sm:h-[65vh]" frameborder="0" />
            </div>

            <!-- Doc footer -->
            <div
              class="border-t px-3 py-2"
              :class="{
                'bg-green-50 dark:bg-green-950/30': decisions[doc.id]?.action === 'accept',
                'bg-destructive/5': decisions[doc.id]?.action === 'reject',
              }"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex min-w-0 items-center gap-2">
                  <span class="truncate text-sm font-medium">{{ formatLabel(doc.name) }}</span>
                  <DocStatusBadge :status="doc.status" class="shrink-0 text-xs" />
                </div>

                <div class="flex shrink-0 items-center gap-1.5">
                  <a
                    :href="doc.file_url!"
                    target="_blank"
                    class="text-muted-foreground hover:text-foreground"
                  >
                    <ExternalLink class="size-3.5" />
                  </a>

                  <template v-if="doc.status === 'uploaded'">
                    <Button
                      size="sm"
                      variant="outline"
                      class="h-7 px-2.5 text-xs"
                      :class="decisions[doc.id]?.action === 'accept'
                        ? 'border-green-500 bg-green-600 text-white hover:bg-green-700 hover:text-white'
                        : ''"
                      @click="toggleDecision(doc.id, 'accept')"
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      class="h-7 px-2.5 text-xs"
                      :class="decisions[doc.id]?.action === 'reject'
                        ? 'border-destructive bg-destructive text-white hover:bg-destructive/90 hover:text-white'
                        : ''"
                      @click="toggleDecision(doc.id, 'reject')"
                    >
                      Reject
                    </Button>
                  </template>
                </div>
              </div>

              <!-- Rejection reason input -->
              <div v-if="decisions[doc.id]?.action === 'reject'" class="mt-2">
                <Input
                  v-model="decisions[doc.id].rejection_reason"
                  placeholder="Rejection reason (required)"
                  class="text-xs"
                />
              </div>
            </div>
          </div>

          <p
            v-if="documents.filter((d) => d.file_url).length === 0"
            class="text-muted-foreground py-6 text-center text-sm"
          >
            No documents with files yet.
          </p>
        </div>

        <DialogFooter class="mt-2 gap-2">
          <span class="text-muted-foreground mr-auto text-xs">
            {{ uploadedDocs.length }} uploaded · {{ Object.keys(decisions).length }} decision{{ Object.keys(decisions).length !== 1 ? 's' : '' }} marked
          </span>
          <Button variant="outline" @click="previewAllOpen = false">Close</Button>
          <Button :disabled="!canSave || bulkProcessing" @click="saveDecisions">
            {{ bulkProcessing ? 'Saving…' : 'Save Changes' }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
